import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import apiService from "../services/api";

const S = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "0.75rem" },
  title: { fontSize: "1.5rem", fontWeight: "700", color: "#001272", margin: 0 },
  syncBtn: { padding: "0.5rem 1.25rem", backgroundColor: "#001272", color: "white", border: "none", borderRadius: "0.375rem", fontSize: "0.875rem", fontWeight: "500", cursor: "pointer" },
  controls: { display: "flex", gap: "0.75rem", marginBottom: "1.5rem", flexWrap: "wrap" },
  searchInput: { padding: "0.5rem 0.75rem", border: "1px solid #e2e8f0", borderRadius: "0.375rem", fontSize: "0.875rem", width: "240px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" },
  card: { background: "white", borderRadius: "0.5rem", padding: "1.25rem", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
  tutorName: { fontSize: "1rem", fontWeight: "600", color: "#001272", margin: "0 0 0.75rem" },
  row: { display: "flex", justifyContent: "space-between", padding: "0.3rem 0", borderBottom: "1px solid #f8fafc", fontSize: "0.8125rem" },
  label: { color: "#64748b" },
  val: { fontWeight: "500", color: "#1e293b", textAlign: "right", maxWidth: "60%" },
  mappedBadge: { display: "inline-block", padding: "0.15rem 0.5rem", backgroundColor: "#dcfce7", color: "#166534", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: "600" },
  unmappedBadge: { display: "inline-block", padding: "0.15rem 0.5rem", backgroundColor: "#fef9c3", color: "#854d0e", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: "600" },
  actions: { display: "flex", gap: "0.5rem", marginTop: "0.875rem" },
  assignBtn: { flex: 1, padding: "0.4rem 0.75rem", backgroundColor: "#001272", color: "white", border: "none", borderRadius: "0.375rem", fontSize: "0.8125rem", cursor: "pointer" },
  removeBtn: { padding: "0.4rem 0.75rem", backgroundColor: "white", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: "0.375rem", fontSize: "0.8125rem", cursor: "pointer" },
  modal: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "1rem" },
  modalBox: { background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "560px", maxHeight: "80vh", display: "flex", flexDirection: "column" },
  modalTitle: { fontSize: "1.125rem", fontWeight: "700", color: "#001272", margin: "0 0 1rem" },
  recipientSearch: { padding: "0.5rem 0.75rem", border: "1px solid #e2e8f0", borderRadius: "0.375rem", fontSize: "0.875rem", width: "100%", marginBottom: "0.75rem", boxSizing: "border-box" },
  recipientList: { flex: 1, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "0.375rem" },
  recipientItem: { padding: "0.625rem 0.875rem", borderBottom: "1px solid #f1f5f9", cursor: "pointer", fontSize: "0.875rem" },
  recipientItemHovered: { backgroundColor: "#eff6ff" },
  recipientName: { fontWeight: "600", color: "#1e293b" },
  recipientMeta: { color: "#64748b", fontSize: "0.75rem", marginTop: "0.125rem" },
  currencyBadge: { display: "inline-block", padding: "0.1rem 0.4rem", backgroundColor: "#eff6ff", color: "#1d4ed8", borderRadius: "4px", fontSize: "0.7rem", fontWeight: "600", marginRight: "0.375rem" },
  cancelBtn: { marginTop: "1rem", width: "100%", padding: "0.5rem", backgroundColor: "#f1f5f9", color: "#475569", border: "none", borderRadius: "0.375rem", fontSize: "0.875rem", cursor: "pointer" },
  empty: { textAlign: "center", color: "#94a3b8", padding: "2rem", fontSize: "0.875rem" },
};

const TutorCard = ({ tutor, mappings, recipients, onAssign, onRemove }) => {
  const mapping = mappings.find(m => m.canonicalTutorId === tutor.canonicalTutorId);
  const recipient = mapping?.wiseRecipient;

  return (
    <div style={S.card}>
      <h3 style={S.tutorName}>{tutor.firstName} {tutor.lastName}</h3>
      <div style={S.row}>
        <span style={S.label}>Email</span>
        <span style={S.val}>{tutor.email || "—"}</span>
      </div>
      <div style={S.row}>
        <span style={S.label}>Wise status</span>
        <span>{recipient
          ? <span style={S.mappedBadge}>Mapped</span>
          : <span style={S.unmappedBadge}>Not mapped</span>
        }</span>
      </div>
      {recipient && (
        <>
          <div style={S.row}>
            <span style={S.label}>Recipient</span>
            <span style={S.val}>{recipient.fullName}</span>
          </div>
          <div style={S.row}>
            <span style={S.label}>Account</span>
            <span style={S.val}>{recipient.longAccountSummary}</span>
          </div>
          <div style={S.row}>
            <span style={S.label}>Currency</span>
            <span style={S.val}>{recipient.currency}</span>
          </div>
        </>
      )}
      <div style={S.actions}>
        <button
          style={S.assignBtn}
          onMouseEnter={e => e.target.style.backgroundColor = "#000d5c"}
          onMouseLeave={e => e.target.style.backgroundColor = "#001272"}
          onClick={() => onAssign(tutor)}
        >
          {recipient ? "Change recipient" : "Assign recipient"}
        </button>
        {recipient && (
          <button
            style={S.removeBtn}
            onClick={() => onRemove(tutor.canonicalTutorId, tutor.firstName + " " + tutor.lastName)}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const RecipientPickerModal = ({ tutor, recipients, onSelect, onClose }) => {
  const [search, setSearch] = useState("");
  const [hoveredId, setHoveredId] = useState(null);

  const filtered = recipients.filter(r => {
    const q = search.toLowerCase();
    return (
      r.fullName.toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.accountSummary || "").toLowerCase().includes(q) ||
      (r.currency || "").toLowerCase().includes(q) ||
      (r.nickname || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <h2 style={S.modalTitle}>
          Select Wise recipient for {tutor.firstName} {tutor.lastName}
        </h2>
        <input
          style={S.recipientSearch}
          placeholder="Search by name, email, account, currency..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
        <div style={S.recipientList}>
          {filtered.length === 0 && (
            <div style={S.empty}>No recipients match your search.</div>
          )}
          {filtered.map(r => (
            <div
              key={r.wiseAccountId}
              style={{
                ...S.recipientItem,
                ...(hoveredId === r.wiseAccountId ? S.recipientItemHovered : {}),
              }}
              onMouseEnter={() => setHoveredId(r.wiseAccountId)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => onSelect(r)}
            >
              <div style={S.recipientName}>
                <span style={S.currencyBadge}>{r.currency}</span>
                {r.fullName}
                {r.nickname && r.nickname !== r.fullName && (
                  <span style={{ color: "#64748b", fontWeight: 400 }}> · {r.nickname}</span>
                )}
              </div>
              <div style={S.recipientMeta}>
                {r.longAccountSummary || r.accountSummary}
                {r.email && ` · ${r.email}`}
              </div>
            </div>
          ))}
        </div>
        <button style={S.cancelBtn} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
};

const WiseMappingPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterMapped, setFilterMapped] = useState("all");
  const [assigningTutor, setAssigningTutor] = useState(null);

  const { data: tutors = [], isLoading: tutorsLoading } = useQuery({
    queryKey: ["tutors-consolidated"],
    queryFn: () => apiService.request("/api/tutors?consolidated=true&size=500"),
    select: data => data?.content ?? [],
  });

  const { data: recipients = [], isLoading: recipientsLoading } = useQuery({
    queryKey: ["wise-recipients"],
    queryFn: () => apiService.getWiseRecipients(),
  });

  const { data: mappings = [], isLoading: mappingsLoading } = useQuery({
    queryKey: ["wise-mappings"],
    queryFn: () => apiService.getWiseMappings(),
  });

  const syncMutation = useMutation({
    mutationFn: () => apiService.triggerWiseSync(),
    onSuccess: data => {
      toast.success(`Sync complete — ${data.upserted} recipients updated`);
      queryClient.invalidateQueries({ queryKey: ["wise-recipients"] });
    },
    onError: () => toast.error("Wise sync failed"),
  });

  const setMappingMutation = useMutation({
    mutationFn: ({ canonicalTutorId, wiseAccountId }) =>
      apiService.setWiseMapping(canonicalTutorId, wiseAccountId),
    onSuccess: () => {
      toast.success("Recipient mapped successfully");
      queryClient.invalidateQueries({ queryKey: ["wise-mappings"] });
      setAssigningTutor(null);
    },
    onError: () => toast.error("Failed to save mapping"),
  });

  const removeMappingMutation = useMutation({
    mutationFn: canonicalTutorId => apiService.removeWiseMapping(canonicalTutorId),
    onSuccess: () => {
      toast.success("Mapping removed");
      queryClient.invalidateQueries({ queryKey: ["wise-mappings"] });
    },
    onError: () => toast.error("Failed to remove mapping"),
  });

  const handleRemove = (canonicalTutorId, name) => {
    if (window.confirm(`Remove Wise mapping for ${name}?`)) {
      removeMappingMutation.mutate(canonicalTutorId);
    }
  };

  const mappedIds = new Set(mappings.map(m => m.canonicalTutorId));

  const filteredTutors = tutors
    .filter(t => {
      const name = `${t.firstName || ""} ${t.lastName || ""} ${t.email || ""}`.toLowerCase();
      if (search && !name.includes(search.toLowerCase())) return false;
      if (filterMapped === "mapped" && !mappedIds.has(t.canonicalTutorId)) return false;
      if (filterMapped === "unmapped" && mappedIds.has(t.canonicalTutorId)) return false;
      return true;
    });

  const isLoading = tutorsLoading || recipientsLoading || mappingsLoading;

  return (
    <div>
      <div style={S.header}>
        <h1 style={S.title}>
          Wise Payment Mappings
          {!isLoading && (
            <span style={{ fontSize: "0.875rem", fontWeight: 400, color: "#64748b", marginLeft: "0.75rem" }}>
              {mappings.length} / {tutors.length} tutors mapped · {recipients.length} recipients in Wise
            </span>
          )}
        </h1>
        <button
          style={S.syncBtn}
          disabled={syncMutation.isPending}
          onMouseEnter={e => e.target.style.backgroundColor = "#000d5c"}
          onMouseLeave={e => e.target.style.backgroundColor = "#001272"}
          onClick={() => syncMutation.mutate()}
        >
          {syncMutation.isPending ? "Syncing…" : "Sync Wise recipients"}
        </button>
      </div>

      <div style={S.controls}>
        <input
          style={S.searchInput}
          placeholder="Search tutors..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {["all", "mapped", "unmapped"].map(f => (
          <button
            key={f}
            style={{
              padding: "0.5rem 1rem",
              border: "1px solid #e2e8f0",
              borderRadius: "0.375rem",
              fontSize: "0.8125rem",
              cursor: "pointer",
              backgroundColor: filterMapped === f ? "#001272" : "white",
              color: filterMapped === f ? "white" : "#475569",
            }}
            onClick={() => setFilterMapped(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {isLoading && <div style={S.empty}>Loading…</div>}

      {!isLoading && filteredTutors.length === 0 && (
        <div style={S.empty}>No tutors match your filters.</div>
      )}

      <div style={S.grid}>
        {filteredTutors.map(tutor => (
          <TutorCard
            key={tutor.canonicalTutorId}
            tutor={tutor}
            mappings={mappings}
            recipients={recipients}
            onAssign={setAssigningTutor}
            onRemove={handleRemove}
          />
        ))}
      </div>

      {assigningTutor && (
        <RecipientPickerModal
          tutor={assigningTutor}
          recipients={recipients}
          onSelect={r => setMappingMutation.mutate({
            canonicalTutorId: assigningTutor.canonicalTutorId,
            wiseAccountId: r.wiseAccountId,
          })}
          onClose={() => setAssigningTutor(null)}
        />
      )}
    </div>
  );
};

export default WiseMappingPage;
